/**
 * Right Sidebar Component
 * 
 * The right sidebar that appears on all pages in the admin interface.
 * Fixed on the right side, same height as left sidebar.
 * Can be toggled open/closed. When closed, shows only icon buttons.
 * Based on reference: app/src/views/private/private-view.vue sidebar section
 */

import { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useContentStore } from '@/stores/content';
import { useRightSidebarSections } from './RightSidebarContext';
import { SidebarDetail } from './SidebarDetail';

/**
 * Global Right Sidebar - rendered at the AppLayout level
 * Shows sections registered by pages via useRightSidebar hook
 */
export function GlobalRightSidebar() {
    const { rightSidebarOpen, toggleRightSidebar } = useContentStore();
    const sections = useRightSidebarSections();

    // If no sections registered, still show the sidebar structure but empty
    const hasSections = sections.length > 0;

    return (
        <aside
            className={cn(
                "shrink-0 border-l border-border bg-muted/30 transition-all duration-200 h-full",
                rightSidebarOpen ? "w-72" : "w-14"
            )}
        >
            <div className="flex h-full flex-col">
                {/* Toggle Button Header */}
                <div className={cn(
                    "flex items-center border-b border-border h-14 shrink-0",
                    rightSidebarOpen ? "justify-end px-3" : "justify-center"
                )}>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleRightSidebar}
                        className="h-8 w-8"
                    >
                        {rightSidebarOpen ? (
                            <ChevronRight className="h-4 w-4" />
                        ) : (
                            <ChevronLeft className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                {/* Content - Expanded or Collapsed */}
                {rightSidebarOpen ? (
                    <ScrollArea className="flex-1">
                        <div className="flex flex-col">
                            {sections.map((section) => (
                                <SidebarDetail
                                    key={section.id}
                                    icon={section.icon}
                                    title={section.title}
                                    badge={section.badge}
                                    defaultOpen={section.defaultOpen}
                                >
                                    {section.content}
                                </SidebarDetail>
                            ))}
                            {!hasSections && (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                    No sidebar sections for this page
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                ) : (
                    <div className="flex flex-col items-center gap-1 py-2 flex-1">
                        {sections.map((section) => (
                            <Button
                                key={section.id}
                                variant="ghost"
                                size="icon"
                                onClick={toggleRightSidebar}
                                className="h-9 w-9"
                                title={section.title}
                            >
                                <span className="text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
                                    {section.icon}
                                </span>
                            </Button>
                        ))}
                    </div>
                )}

                {/* Activity Log button at bottom */}
                <div className={cn(
                    "border-t border-border shrink-0",
                    rightSidebarOpen ? "p-2" : "p-1 flex justify-center"
                )}>
                    {rightSidebarOpen ? (
                        <Button variant="ghost" className="w-full justify-start gap-2 text-sm">
                            <Clock className="h-4 w-4" />
                            Activity Log
                        </Button>
                    ) : (
                        <Button variant="ghost" size="icon" className="h-9 w-9" title="Activity Log">
                            <Clock className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>
        </aside>
    );
}

interface RightSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    children: ReactNode;
    className?: string;
}

/**
 * Legacy RightSidebar for backward compatibility
 * Use GlobalRightSidebar at AppLayout level instead
 */
export function RightSidebar({
    isOpen,
    onToggle,
    children,
    className,
}: RightSidebarProps) {
    return (
        <aside
            className={cn(
                "shrink-0 border-l border-border bg-muted/30 transition-all duration-200",
                isOpen ? "w-72" : "w-14",
                className
            )}
        >
            <div className="flex h-full flex-col">
                <div className={cn(
                    "flex items-center border-b border-border h-14",
                    isOpen ? "justify-end px-3" : "justify-center"
                )}>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggle}
                        className="h-8 w-8"
                    >
                        {isOpen ? (
                            <ChevronRight className="h-4 w-4" />
                        ) : (
                            <ChevronLeft className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                {isOpen ? (
                    <ScrollArea className="flex-1">
                        {children}
                    </ScrollArea>
                ) : (
                    <div className="flex flex-col items-center gap-1 py-2">
                        {/* Icons shown when collapsed */}
                    </div>
                )}
            </div>
        </aside>
    );
}
